import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  getUserById,
  createUser,
  updateUser,
  getRoles,
  logError,
  createCarrier,
  createSalesRep,
  createUserIncludingSalesRep
} from '../api';
import { Button, Form, Modal, InputGroup } from 'react-bootstrap';
import { validatePasswordsMatch, validatePasswordComplexity } from '../utils/PasswordUtils';
import { isAdmin as checkIsAdmin } from '../services/AuthService';
import FormContainer from './Global/FormContainer';
import { FaEye, FaEyeSlash } from 'react-icons/fa';

import CarrierForm from './CarrierForm';
import SalesRepForm from './SalesRepForm';

const UserForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [username, setUsername] = useState('');
  const [roles, setRoles] = useState([]);
  const [selectedRoles, setSelectedRoles] = useState([]);
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isEdit, setIsEdit] = useState(false);
  const [passwordError, setPasswordError] = useState('');
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [touchedFields, setTouchedFields] = useState({ password: false, confirm: false });
  const [visibility, setVisibility] = useState({ password: false, confirm: false });

  const [carrierData, setCarrierData] = useState(null);
  const [salesRepData, setSalesRepData] = useState(null);

  const [showCarrierModal, setShowCarrierModal] = useState(false);
  const [draftSalesRep, setDraftSalesRep] = useState(null);
  const [showSalesRepModal, setShowSalesRepModal] = useState(false);

  const isAdmin = checkIsAdmin();

  useEffect(() => {
    initializeForm();
  }, [id]);

  const initializeForm = async () => {
    try {
      await loadRoles();
      if (id) await loadUser();
    } catch (error) {
      logError(error);
    }
  };

  const loadRoles = async () => {
    try {
      const rolesData = await getRoles();
      const filteredRoles = isAdmin ? rolesData : rolesData.filter((role) => role.id !== 1);
      setRoles(filteredRoles);
    } catch (error) {
      logError(error);
    }
  };

  const loadUser = async () => {
    try {
      const user = await getUserById(id);
      setUsername(user.username);
      setSelectedRoles(user.roles.map((role) => role.id));
      setIsEdit(true);
    } catch (error) {
      logError(error);
    }
  };

  const handleInputChange = (setter) => (e) => {
    setter(e.target.value);
    setHasUnsavedChanges(true);
  };

  const toggleRole = (roleId) => {
        setSelectedRoles((prev) => {
            const updated = prev.includes(roleId)
                ? prev.filter((id) => id !== roleId)
                : [...prev, roleId];
            return updated;
        });
    setHasUnsavedChanges(true);
  };

  const toggleVisibility = (field) => {
    setVisibility((prev) => ({ ...prev, [field]: !prev[field] }));
  };

  const handleSubmit = async (e) => {
  e.preventDefault();

  // 1. Validación de contraseña
  if (password || confirmPassword) {
    let pwdError = validatePasswordsMatch(password, confirmPassword);
    if (pwdError) {
      setPasswordError(pwdError);
      return;
    }
    pwdError = validatePasswordComplexity(password);
    if (pwdError) {
      setPasswordError(pwdError);
      return;
    }
  }

  // 2. Prevención de asignar rol Admin si no tienes permisos
  if (!isAdmin && selectedRoles.includes(1)) {
    alert('No tienes permisos para asignar el rol de Admin.');
    return;
  }

  // 3. Preparamos el payload base para el usuario
  const userPayload = {
    username,
    roles: selectedRoles,
    ...(password && { password })
  };

  try {
    if (isEdit) {
      // --- Actualizar usuario existente ---
      await updateUser(id, userPayload);

    } else {
      // --- Crear usuario nuevo ---
      if (salesRepData) {
        // Creamos usuario + salesRep en una sola llamada
        await createUserIncludingSalesRep({
          userRequest: userPayload,
          salesRepRequest: { ...salesRepData }
        });

        // Si además tienes carrierData, tras la creación en backend
        // podrías enlazarlo aquí si tu endpoint lo soporta,
        // o bien hacer una llamada a createCarrier si es necesario:
        if (carrierData) {
          // asumiendo que tu backend rellena el nuevo userId en la respuesta,
          // tendrías que extraerlo. Si no, podrías seguir usando createCarrier
          // con el userId correcto.
          // Ejemplo “por si acaso”:
          const createdUser = await createUser(userPayload);
          await createCarrier({ ...carrierData, userId: createdUser.id });
        }

      } else {
        // No hay salesRep: creamos solo usuario
        const createdUser = await createUser(userPayload);
        const newUserId = createdUser.id;

        // Si hay carrierData, lo enlazamos al user recién creado
        if (carrierData) {
          await createCarrier({ ...carrierData, userId: newUserId });
        }
      }
    }

    // 4. Tras guardarlo todo...
    setHasUnsavedChanges(false);
    navigate('/users');

  } catch (error) {
    const status = error?.response?.status;
    if (status === 409) {
        alert('Esta abreviatura ya está en uso. Elige otra');
      }
    else{
      logError(error);
      // Aquí podrías mostrar un mensaje de error al usuario, p.e.:
      alert('Hubo un error al guardar. Comprueba la consola para más detalles.');
    }
  }
};


  const handleCancel = () => {
    if (hasUnsavedChanges) {
      if (window.confirm('Tienes cambios sin guardar. ¿Seguro que quieres salir?')) {
        navigate(-1);
      }
    } else {
      navigate(-1);
    }
  };

  return (
    <FormContainer title={isEdit ? 'Editar usuario' : 'Crear usuario'}>
      <Form onSubmit={handleSubmit}>
        <Form.Group controlId="username" className="mb-3">
          <Form.Label>Username</Form.Label>
          <Form.Control
            type="text"
            placeholder="Enter username"
            value={username}
            onChange={handleInputChange(setUsername)}
            autoComplete="off"
            required
            data-testid="username-input"
          />
        </Form.Group>

        <Form.Group controlId="roles" className="mb-3">
          <Form.Label>Roles</Form.Label>
          <div>
            {roles.map((role) => (
              <Form.Check
                key={role.id}
                type="checkbox"
                id={role.id}
                label={role.name}
                checked={selectedRoles.includes(role.id)}
                onChange={() => toggleRole(role.id)}
                data-testid={`role-checkbox-${role.id}`}
              />
            ))}
          </div>
        </Form.Group>

        {!isEdit && selectedRoles.includes(4) && (
          <Button 
            variant="outline-primary" 
            className="mt-2" 
            onClick={() => setShowCarrierModal(true)}
            data-testid="create-carrier-button"
          >
            Crear transportista y asignarlo
          </Button>
        )}

        {!isEdit && selectedRoles.includes(3) && (
          <Button 
            variant="outline-primary" 
            className="mt-2 ms-2" 
            onClick={() => setShowSalesRepModal(true)}
            data-testid="create-salesrep-button"
          >
            Crear comercial y asignarlo
          </Button>
        )}

        <p className="mb-1 mt-3">La contraseña debe contener:</p>
        <ul>
          <li>Al menos 8 caracteres</li>
          <li>Una letra mayúscula</li>
          <li>Una letra minúscula</li>
          <li>Un número</li>
        </ul>

        <Form.Group controlId="password" className="mb-3 mt-3">
          <Form.Label>Contraseña</Form.Label>
          <InputGroup>
            <Form.Control
              type={visibility.password ? 'text' : 'password'}
              placeholder="Nueva contraseña"
              value={password}
              onChange={handleInputChange(setPassword)}
              onBlur={() => setTouchedFields((prev) => ({ ...prev, password: true }))}
              autoComplete="new-password"
              data-testid="password-input"
            />
            <InputGroup.Text onClick={() => toggleVisibility('password')} style={{ cursor: 'pointer' }}>
              {visibility.password ? <FaEyeSlash /> : <FaEye />}
            </InputGroup.Text>
          </InputGroup>
          {touchedFields.password && validatePasswordComplexity(password) && (
            <div className="form-text text-danger">{validatePasswordComplexity(password)}</div>
          )}
        </Form.Group>

        <Form.Group controlId="confirmPassword" className="mb-3">
          <Form.Label>Confirmar contraseña</Form.Label>
          <InputGroup>
            <Form.Control
              type={visibility.confirm ? 'text' : 'password'}
              placeholder="Confirmar nueva contraseña"
              value={confirmPassword}
              onChange={handleInputChange(setConfirmPassword)}
              onBlur={() => setTouchedFields((prev) => ({ ...prev, confirm: true }))}
              autoComplete="new-password"
              data-testid="confirm-password-input"
            />
            <InputGroup.Text onClick={() => toggleVisibility('confirm')} style={{ cursor: 'pointer' }}>
              {visibility.confirm ? <FaEyeSlash /> : <FaEye />}
            </InputGroup.Text>
          </InputGroup>
          {touchedFields.confirm && validatePasswordsMatch(password, confirmPassword) && (
            <div className="form-text text-danger">{validatePasswordsMatch(password, confirmPassword)}</div>
          )}
        </Form.Group>

        {passwordError && <p className="text-danger">{passwordError}</p>}

        <div className="d-flex justify-content-between mt-4">
          <Button variant="primary" type="submit" data-testid="submit-button">
            {isEdit ? 'Guardar cambios' : 'Crear usuario'}
          </Button>
          <Button variant="secondary" onClick={handleCancel} data-testid="cancel-button">
            Cancelar
          </Button>
        </div>
      </Form>
      {/* Modal Carrier */}
      <Modal
          show={showCarrierModal}
          onHide={() => setShowCarrierModal(false)}
          backdrop="static"
          size="lg"
      >
        <Modal.Header closeButton>
          <Modal.Title data-testid="carrier-modal-title">Crear Carrier</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <CarrierForm
            embedded
            onSave={(data) => {
              setCarrierData(data);
              setShowCarrierModal(false);
            }}
            onCancel={() => setShowCarrierModal(false)}
          />
        </Modal.Body>
      </Modal>
      {/* Modal SalesRep */}
      <Modal 
          show={showSalesRepModal}
          onHide={() => setShowSalesRepModal(false)}
          backdrop="static"
          size="lg"
      >
        <Modal.Header closeButton>
          <Modal.Title data-testid="salesrep-modal-title">Crear comercial</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <SalesRepForm
            embedded
            initialData={draftSalesRep}
            onChange={setDraftSalesRep}  
            onSave={(finalData) => {
              setSalesRepData(finalData);
              setShowSalesRepModal(false);
            }}
            onCancel={() => setShowSalesRepModal(false)}
          />
        </Modal.Body>
      </Modal>
    </FormContainer>
  );
};

export default UserForm;
